import { createContext, useContext, useEffect, useState } from "react";
import server from "../config/server";
import { useUserStore } from "./userStore";
import { useAuth } from "./auth";
import { publish, subscribe, unsubscribe } from "../utils/events";
import axios from "axios";

const SSEContext = createContext({
	sseReceived: null,
	isConnected: false,
	subscribedActions: [],
	subscribeToActions: (actions, unitId) => { },
	unsubscribeToActions: (actions, unitId) => { },
});

let eventSource = null;

export const SSEProvider = ({ children }) => {
	const { user, accessToken } = useAuth();
	const { selectedCondominio } = useUserStore();
	const [sseReceived, setSseReceived] = useState(null);
	const [isConnected, setIsConnected] = useState(false);
	const [subscribedActions, setSubscribedActions] = useState([]);

	useEffect(() => {
		const onLogout = () => {
			eventSource?.close();
		}
		subscribe(`logout`, onLogout);
		return () => {
			unsubscribe('logout', onLogout);
		}
	}, []);

	useEffect(() => {
		if (!user?.userData?.userExternalId) return;
		if (!accessToken) return;

		eventSource = new EventSource(
			selectedCondominio ?
				`${server.url}/sse/events/condominiums/${selectedCondominio}/${user.userData.userExternalId}/${accessToken}` :
				`${server.url}/sse/events/root/${user.userData.userExternalId}/${accessToken}`
		);
		eventSource.onmessage = (event) => {
			const eventReceived = JSON.parse(event.data);
			setSseReceived(eventReceived);
			publish('onSseReceived', eventReceived);
		};
		eventSource.onopen = (connected) => {
			console.log("SSE Connected", connected);
			setIsConnected(true);
			publish('onSseConnect', { connected });
		};
		eventSource.onerror = (error) => {
			console.log("EventSource error", error);
			console.log(error);
			setIsConnected(false);
			publish('onSseDisconnect', { error });
		};
		return () => {
			eventSource.close();
		};
	}, [user?.userData?.userExternalId, selectedCondominio]);

	const subscribeToActions = async (actions, unitId) => {
		const response = await axios.post(`${server.url}/sse/subscribe`, {
			actions,
			unitId,
		});
		setSubscribedActions(Array.from(
			new Set([
				...actions,
				...subscribedActions,
			])
		));
		return response.data;
	}

	const unsubscribeToActions = async (actions, unitId) => {
		const actionsToUnsub = actions.filter(action => subscribedActions.includes(action)) ?? [];
		if (actionsToUnsub.length === 0) {
			return;
		}
		const response = await axios.post(`${server.url}/sse/unsubscribe`, {
			actions: actionsToUnsub,
			unitId,
		});
		setSubscribedActions(prev => prev.filter(action => !actionsToUnsub.includes(action)));
		return response.data;
	}

	return (
		<SSEContext.Provider value={{
			sseReceived,
			isConnected,
			subscribeToActions,
			unsubscribeToActions,
			subscribedActions
		}}>
			{children}
		</SSEContext.Provider>
	);
};

export function useSSEContext() {
	const context = useContext(SSEContext);

	return context;
}
